/**
 * useVoiceDictation — record short voice clips in the browser and POST them
 * to /api/web/voice/dictate, returning the transcript.
 *
 * Strategy:
 *   1. Ask for microphone permission only when the user clicks the button
 *      (lazy → no scary prompt on page load).
 *   2. Use MediaRecorder with the codec the browser supports best
 *      (audio/webm;codecs=opus → wide support, falls back automatically).
 *   3. Cap recording at MAX_SECONDS so a forgotten mic doesn't blow credits.
 *   4. On stop → POST as multipart/form-data → callback receives the
 *      transcript (no LLM call, only Whisper).
 *
 * Returns:
 *   { supported, recording, start(), stop(), level }
 *   - `start(onTranscript)`: begins recording. onTranscript called once when
 *     transcription succeeds.
 *   - `stop()`: ends recording → triggers upload + onTranscript callback.
 *   - `level`: a 0..1 number representing the input volume for visual feedback.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import webApi from "@/lib/webApi";

const MAX_SECONDS = 60;

export default function useVoiceDictation() {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [level, setLevel] = useState(0);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const onDoneRef = useRef(null);
  const timeoutRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);

  const supported = typeof navigator !== "undefined"
    && typeof navigator.mediaDevices !== "undefined"
    && typeof window.MediaRecorder !== "undefined";

  const cleanup = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    timeoutRef.current = null;
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (closeErr) {
        console.debug("[useVoiceDictation] audioContext.close failed:", closeErr?.message);
      }
      audioCtxRef.current = null;
    }
    setLevel(0);
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const pickMime = () => {
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/mp4",
      "",
    ];
    for (const m of candidates) {
      if (!m || (window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported(m))) {
        return m;
      }
    }
    return "";
  };

  const ext = (mime) => {
    if (!mime) return "webm";
    if (mime.includes("webm")) return "webm";
    if (mime.includes("ogg")) return "ogg";
    if (mime.includes("mp4")) return "m4a";
    return "webm";
  };

  const start = useCallback(async (onTranscript) => {
    if (!supported) return false;
    if (recording || uploading) return false;
    onDoneRef.current = onTranscript || null;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // VU-meter for visual feedback
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) {
          const ctx = new AC();
          const src = ctx.createMediaStreamSource(stream);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 128;
          src.connect(analyser);
          audioCtxRef.current = ctx;
          const data = new Uint8Array(analyser.frequencyBinCount);
          const tick = () => {
            analyser.getByteTimeDomainData(data);
            let sum = 0;
            for (let i = 0; i < data.length; i++) {
              const v = (data[i] - 128) / 128;
              sum += v * v;
            }
            setLevel(Math.min(1, Math.sqrt(sum / data.length) * 4));
            rafRef.current = requestAnimationFrame(tick);
          };
          tick();
        }
      } catch (vuErr) {
        // VU meter is purely cosmetic — log but never propagate.
        console.debug("[useVoiceDictation] VU meter setup skipped:", vuErr?.message);
      }

      const mime = pickMime();
      const rec = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        cleanup();
        setRecording(false);
        if (blob.size < 1000) {
          // Too short
          return;
        }
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, `dictation.${ext(mime)}`);
          const r = await webApi.post("/voice/dictate", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const text = (r.data?.transcript || "").trim();
          if (onDoneRef.current) onDoneRef.current(text, r.data);
        } finally {
          setUploading(false);
          onDoneRef.current = null;
        }
      };
      rec.start();
      setRecording(true);
      timeoutRef.current = setTimeout(() => stop(), MAX_SECONDS * 1000);
      return true;
    } catch (e) {
      cleanup();
      setRecording(false);
      // Surface the error via the callback so the UI can show toast
      if (onDoneRef.current) onDoneRef.current("", { error: e?.message || "mic_error" });
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported, recording, uploading, cleanup]);

  const stop = useCallback(() => {
    if (!recorderRef.current) return;
    try {
      if (recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
    } catch (stopErr) {
      // Some browsers throw if you call stop() in the wrong state.
      console.debug("[useVoiceDictation] recorder.stop failed:", stopErr?.message);
    }
  }, []);

  return { supported, recording, uploading, level, start, stop };
}
